import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import DocumentCard from './DocumentCard';
import AnnexureUploadModal from './AnnexureUploadModal';
import DocumentService from 'utils/documentService';
import httpClient from 'utils/httpClient';

const DocumentsSection = ({
  sowId,
  projectId,
  documents = [],
  sowData,
  refreshToken = 0,
  resetToken = 0,
  onDocumentsUpdate,
  onSowStatusUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({});
  const [fetchedDocuments, setFetchedDocuments] = useState([]);
  const [fetchingDocuments, setFetchingDocuments] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [documentContext, setDocumentContext] = useState(
    sowData?.document_context || null
  );
  const [annexureModalOpen, setAnnexureModalOpen] = useState(false);

  const isTimesheetView = sowData?.budget_model === 'hourly';

  // Helper function to calculate generation status based on API documents data
  const getGenerationStatusFromDocuments = (
    documentsArray,
    milestoneCount,
    isTimesheet
  ) => {
    const hasDocuments = documentsArray && documentsArray.length > 0;
    const canGenerate = isTimesheet || milestoneCount > 0;
    const canGeneratePDF = hasDocuments;

    const requirements = [];
    // Only show milestone requirement if documents don't exist yet
    // If documents already exist, user doesn't need to generate new ones
    if (!isTimesheet && !canGenerate && !hasDocuments) {
      requirements.push('At least 1 milestone is required.');
    }
    if (!canGeneratePDF && !hasDocuments) {
      requirements.push('Generate SOW');
    }

    return {
      canGenerateDocument: canGenerate,
      canGeneratePDF,
      hasExistingDocuments: hasDocuments,
      requirements,
      documentsCount: documentsArray?.length || 0,
    };
  };

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!sowId) {
        setFetchedDocuments([]);
        setDocumentContext(sowData?.document_context || null);
        return;
      }

      try {
        setFetchingDocuments(true);
        setFetchError(null);

        const response = await httpClient.get(
          `/api/v1/project-management/sows/${sowId}/documents/`
        );

        if (response?.status === true) {
          if (response?.data?.documents) {
            // API already returns documents with correct structure (audience, etc.)
            setFetchedDocuments(
              Array.isArray(response.data.documents)
                ? response.data.documents
                : []
            );
          } else {
            setFetchedDocuments([]);
          }

          if (response?.data?.document_context) {
            setDocumentContext(response.data.document_context);
          }
        } else {
          setFetchedDocuments([]);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setFetchError(error.message || 'Failed to fetch documents');
        setFetchedDocuments([]);
      } finally {
        setFetchingDocuments(false);
      }
    };

    fetchDocuments();
  }, [sowId, refreshToken]);

  useEffect(() => {
    if (!resetToken) {
      return;
    }

    setFetchedDocuments([]);
    setDocumentContext({
      action: 'generate_sow',
      has_signed_sow: false,
      has_draft_sow: true,
      has_draft_addendum: false,
      draft_sow_id: sowData?.id || sowId || null,
      draft_addendum_id: null,
    });
  }, [resetToken, sowData?.document_context]);

  // Use fetched documents instead of prop
  const displayDocuments =
    fetchedDocuments.length > 0 ? fetchedDocuments : documents;
  const hasDocuments = displayDocuments && displayDocuments.length > 0;
  const generationStatus = getGenerationStatusFromDocuments(
    displayDocuments,
    sowData?.milestone_count || 0,
    isTimesheetView
  );

  // Determine generation action and button text based on document context
  const isSowSigned =
    sowData?.status === 'signed' || documentContext?.has_signed_sow === true;
  const shouldGenerateAddendum =
    documentContext?.action === 'generate_addendum' &&
    documentContext?.has_signed_sow === true &&
    documentContext?.has_draft_addendum === true;
  // const buttonText = shouldGenerateAddendum
  //   ? 'Generate Addendum'
  //   : 'Generate SOW';
  const buttonText = 'Generate SOW';
  const loadingText = shouldGenerateAddendum
    ? 'Generating...'
    : 'Generating...';
  const successMessage = shouldGenerateAddendum
    ? 'Addendum generated successfully'
    : 'Both documents generated successfully';
  // const createAddendumText = 'Create Addendum';
  // const createAddendumLoadingText = 'Creating Addendum...';

  const handleGenerateDocuments = async () => {
    try {
      setLoading(true);

      // Generate documents based on context (document or addendum)
      const response = await DocumentService.generateTemplate(
        sowId,
        ['client', 'freelancer'],
        documentContext
      );
      const formattedResponse = DocumentService.formatGenerationResponse(
        response,
        'document'
      );

      if (formattedResponse.success) {
        toast.success(successMessage);

        const hasGeneratedDocs = Boolean(
          formattedResponse.updatedDocuments?.client_document ||
            formattedResponse.updatedDocuments?.freelancer_document
        );

        // Refresh documents from API
        if (sowId) {
          const refreshResponse = await httpClient.get(
            `/api/v1/project-management/sows/${sowId}/documents/`
          );
          if (refreshResponse?.status === true) {
            if (refreshResponse?.data?.documents) {
              setFetchedDocuments(
                Array.isArray(refreshResponse.data.documents)
                  ? refreshResponse.data.documents
                  : []
              );
            }

            if (refreshResponse?.data?.document_context) {
              setDocumentContext(refreshResponse.data.document_context);
            }
          }
        }

        // Update documents in state (for backward compatibility)
        if (onDocumentsUpdate && formattedResponse.updatedDocuments) {
          onDocumentsUpdate(formattedResponse.updatedDocuments);
        }

        if (!shouldGenerateAddendum && hasGeneratedDocs && onSowStatusUpdate) {
          onSowStatusUpdate({ status: 'active' });
        }
      } else {
        toast.error(formattedResponse.message);
      }
    } catch (error) {
      console.error('Document generation error:', error);
      toast.error(
        error.message ||
          `Failed to generate ${
            shouldGenerateAddendum ? 'addendum' : 'documents'
          }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWithAnnexures = async (files) => {
    try {
      setLoading(true);

      // Build the same target/endpoint the JSON path uses (handles addendum routing)
      const target = DocumentService.resolveTargetIds(sowId, documentContext);
      const endpoint = DocumentService.buildGenerationEndpoint(
        target,
        'generate-template'
      );

      const formData = new FormData();
      formData.append('audience[0]', 'client');
      formData.append('audience[1]', 'freelancer');
      if (target.action) {
        formData.append('action', target.action);
      }
      files.forEach((file) => formData.append('annexures', file));

      // Content-Type: undefined removes the instance-level application/json so
      // axios/browser set the multipart boundary correctly. Per-request only.
      const response = await httpClient.post(endpoint, formData, {
        headers: { 'Content-Type': undefined },
      });
      const formattedResponse = DocumentService.formatGenerationResponse(
        response,
        'document'
      );

      if (formattedResponse.success) {
        toast.success(successMessage);

        const hasGeneratedDocs = Boolean(
          formattedResponse.updatedDocuments?.client_document ||
            formattedResponse.updatedDocuments?.freelancer_document
        );

        // Refresh documents from API
        if (sowId) {
          const refreshResponse = await httpClient.get(
            `/api/v1/project-management/sows/${sowId}/documents/`
          );
          if (refreshResponse?.status === true) {
            if (refreshResponse?.data?.documents) {
              setFetchedDocuments(
                Array.isArray(refreshResponse.data.documents)
                  ? refreshResponse.data.documents
                  : []
              );
            }

            if (refreshResponse?.data?.document_context) {
              setDocumentContext(refreshResponse.data.document_context);
            }
          }
        }

        // Update documents in state (for backward compatibility)
        if (onDocumentsUpdate && formattedResponse.updatedDocuments) {
          onDocumentsUpdate(formattedResponse.updatedDocuments);
        }

        if (!shouldGenerateAddendum && hasGeneratedDocs && onSowStatusUpdate) {
          onSowStatusUpdate({ status: 'active' });
        }
      } else {
        toast.error(formattedResponse.message);
      }
    } catch (error) {
      console.error('Document generation error:', error);
      toast.error(
        error.message ||
          `Failed to generate ${
            shouldGenerateAddendum ? 'addendum' : 'documents'
          }`
      );
    } finally {
      setLoading(false);
    }
  };

  // const handleSign = async () => {
  //   try {
  //     setActivateLoading(true);
  //     const response = await httpClient.post(
  //       `/api/v1/project-management/sows/${sowId}/sign/`
  //     );
  //     if (response.status === true) {
  //       toast.success(response.message);

  //       // Refresh documents from API after signing
  //       if (sowId) {
  //         const refreshResponse = await httpClient.get(
  //           `/api/v1/project-management/sows/${sowId}/documents/`
  //         );
  //         if (refreshResponse?.status === true) {
  //           if (refreshResponse?.data?.documents) {
  //             setFetchedDocuments(
  //               Array.isArray(refreshResponse.data.documents)
  //                 ? refreshResponse.data.documents
  //                 : []
  //             );
  //           }

  //           if (refreshResponse?.data?.document_context) {
  //             setDocumentContext(refreshResponse.data.document_context);
  //           }
  //         }
  //       }

  //       // Update sow status in parent component
  //       if (onSowStatusUpdate && response.data) {
  //         onSowStatusUpdate({
  //           status: response.data.status,
  //           signed_at: response.data.signed_at,
  //           project_status: response.data.project_status,
  //         });
  //       }
  //     } else {
  //       toast.error(response.message);
  //     }
  //   } catch (error) {
  //     console.error('SOW signing error:', error);
  //     toast.error(error.message || 'Failed to sign SOW');
  //   } finally {
  //     setActivateLoading(false);
  //   }
  // };

  const handleGeneratePDF = async (docType) => {
    try {
      setPdfLoading((prev) => ({ ...prev, [docType]: true }));

      const response = await DocumentService.generatePDF(
        sowId,
        docType,
        documentContext
      );
      const formattedResponse = DocumentService.formatGenerationResponse(
        response,
        'pdf'
      );

      if (formattedResponse.success) {
        toast.success(`PDF generated successfully`);

        // Refresh documents from API
        if (sowId) {
          const refreshResponse = await httpClient.get(
            `/api/v1/project-management/sows/${sowId}/documents/`
          );
          if (refreshResponse?.status === true) {
            if (refreshResponse?.data?.documents) {
              setFetchedDocuments(
                Array.isArray(refreshResponse.data.documents)
                  ? refreshResponse.data.documents
                  : []
              );
            }

            if (refreshResponse?.data?.document_context) {
              setDocumentContext(refreshResponse.data.document_context);
            }
          }
        }

        // Update documents in state immediately (for backward compatibility)
        if (onDocumentsUpdate && formattedResponse.updatedDocuments) {
          onDocumentsUpdate(formattedResponse.updatedDocuments);
        }
      } else {
        toast.error(formattedResponse.message);
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(error.message || 'Failed to generate PDF');
    } finally {
      setPdfLoading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  const handleViewPdf = (pdfLink) => {
    window.open(pdfLink, '_blank');
  };

  const handleSendEmail = async (documentId, docType) => {
    toast('Coming Soon.');
    // try {
    //   setLoading(true);
    //   const response = await DocumentService.sendDocumentEmail(
    //     sowId,
    //     documentId
    //   );
    //   toast.success('Email sent successfully');

    //   // Update the specific document's email_sent_at
    //   if (onDocumentsUpdate) {
    //     const documentKey =
    //       docType === 'client' ? 'client_document' : 'freelancer_document';

    //     onDocumentsUpdate({
    //       [documentKey]: {
    //         ...sowData?.[documentKey],
    //         email_sent_at:
    //           response?.data?.email_sent_at || new Date().toISOString(),
    //       },
    //     });
    //   }
    // } catch (error) {
    //   console.error('Email sending error:', error);
    //   toast.error(error.message || 'Failed to send email');
    // } finally {
    //   setLoading(false);
    // }
  };

  return (
    <>
      <Paper sx={{ padding: 3, borderRadius: 2 }}>
        <Box
          display='flex'
          justifyContent='space-between'
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          marginBottom={2}
          gap={2}
        >
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              SOW Documents
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Generate, view, download, and send documents.
            </Typography>
          </Box>

          <Box
            display='flex'
            gap={1}
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            {hasDocuments ? (
              !isSowSigned ? (
                null
                // <Button
                //   variant='contained'
                //   disabled={!sowId || activateLoading || !hasDocuments}
                //   onClick={handleSign}
                //   aria-label='Mark as Signed'
                //   startIcon={
                //     activateLoading ? <CircularProgress size={16} /> : null
                //   }
                // >
                //   Mark as Signed
                // </Button>
              ) : null
            ) : (
              // <Button
              //   variant='contained'
              //   disabled={!sowId || loading}
              //   onClick={handleGenerateDocuments}
              //   aria-label='Create addendum'
              //   startIcon={loading ? <CircularProgress size={16} /> : null}
              // >
              //   {loading ? createAddendumLoadingText : createAddendumText}
              // </Button>
              <Button
                variant='contained'
                disabled={
                  !sowId || loading || !generationStatus?.canGenerateDocument
                }
                onClick={() => setAnnexureModalOpen(true)}
                aria-label={
                  shouldGenerateAddendum ? 'Generate addendum' : 'Generate SOW'
                }
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? loadingText : buttonText}
              </Button>
            )}
          </Box>
        </Box>

        {hasDocuments && !fetchingDocuments && (
          <Box display='flex' flexWrap='wrap' gap={2}>
            {displayDocuments.map((document, index) => (
              <DocumentCard
                key={document.id || document.audience || `document-${index}`}
                document={document}
                onSendEmail={() =>
                  handleSendEmail(document.id, document.audience)
                }
                onViewPdf={handleViewPdf}
                onGeneratePDF={() => handleGeneratePDF(document.audience)}
                loading={loading}
                pdfLoading={pdfLoading[document.audience] || false}
              />
            ))}
          </Box>
        )}

        {/* Loading state while fetching documents */}
        {fetchingDocuments && (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            py={4}
          >
            <CircularProgress size={24} />
            <Typography variant='body2' color='text.secondary' ml={2}>
              Loading documents...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {fetchError && !fetchingDocuments && (
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography variant='body2'>{fetchError}</Typography>
          </Alert>
        )}

        {/* Generation Status Alert */}
        {!fetchingDocuments && generationStatus?.requirements?.length > 0 && (
          <Alert
            severity='warning'
            sx={{
              mb: 2,
              backgroundColor: 'transparent',
              '& .MuiAlert-icon': {
                color: 'warning.main',
              },
            }}
          >
            <Typography variant='body2' color='text.warning'>
              {generationStatus.requirements.join(' ')}
            </Typography>
          </Alert>
        )}
      </Paper>

      <AnnexureUploadModal
        open={annexureModalOpen}
        onSkip={() => {
          setAnnexureModalOpen(false);
          handleGenerateDocuments();
        }}
        onSubmit={(files) => {
          setAnnexureModalOpen(false);
          handleGenerateWithAnnexures(files);
        }}
        isLoading={loading}
        error={null}
      />
    </>
  );
};

export default DocumentsSection;
